import { useCallback, useRef, useState } from "react";

const MOVE_THRESHOLD_PX = 7;

export function shouldUseNativeDockClick(pointerType) {
  return pointerType === "mouse";
}

function dockButtonAt(root, x, y) {
  if (!root || typeof document === "undefined") return null;
  const element = document.elementFromPoint(x, y);
  const button = element?.closest?.("button");
  if (!button || !root.contains(button) || button.disabled) return null;
  return button;
}

function dockButtonKey(button) {
  if (!button) return null;
  return (
    button.dataset.dockItem ||
    button.getAttribute("aria-label") ||
    button.textContent?.trim() ||
    null
  );
}

function closestDockOption(root, x, y) {
  if (!root) return null;
  let closest = null;
  let closestDistance = Number.POSITIVE_INFINITY;
  root.querySelectorAll?.("button[data-dock-item]").forEach((button) => {
    if (button.disabled) return;
    const rect = button.getBoundingClientRect();
    const distance = Math.hypot(
      x - (rect.left + rect.width / 2),
      y - (rect.top + rect.height / 2)
    );
    if (distance < closestDistance) {
      closest = button;
      closestDistance = distance;
    }
  });
  return closest;
}

export function useDockPointerSelection({
  onOptionSelected,
  onInteraction,
  onTouchOptionSelected,
} = {}) {
  const [hoveredKey, setHoveredKey] = useState(null);
  const dragRef = useRef(null);
  const lastPointerTypeRef = useRef(null);
  const dispatchingRef = useRef(false);
  const suppressClickRef = useRef(false);
  const suppressTimerRef = useRef(null);

  const dispatchButtonClick = useCallback((button) => {
    if (!button) return;
    suppressClickRef.current = true;
    dispatchingRef.current = true;
    button.click();
    dispatchingRef.current = false;
    if (suppressTimerRef.current) window.clearTimeout(suppressTimerRef.current);
    suppressTimerRef.current = window.setTimeout(() => {
      suppressClickRef.current = false;
      suppressTimerRef.current = null;
    }, 400);
  }, []);

  const clearDrag = useCallback(() => {
    dragRef.current = null;
    setHoveredKey(null);
  }, []);

  const onPointerDown = useCallback((event) => {
    lastPointerTypeRef.current = event.pointerType || null;
    // No desktop, o click nativo é a fonte única da ação. A seleção por
    // arrastamento existe para toque/caneta; acompanhar também o rato fazia
    // pequenos movimentos entre mousedown e mouseup sintetizarem e depois
    // suprimirem cliques perfeitamente válidos.
    if (shouldUseNativeDockClick(event.pointerType)) return;
    const root = event.currentTarget;
    const button = event.target?.closest?.("button");
    if (!button || !root.contains(button)) return;
    onInteraction?.();
    if (button.dataset.dockToggle === "true") {
      // O puxador abre no instante do toque; não espera pelo click sintetizado
      // pelo iOS depois de o dedo levantar.
      event.preventDefault();
      dragRef.current = {
        root,
        pointerId: event.pointerId,
        pointerType: event.pointerType,
        startX: event.clientX,
        startY: event.clientY,
        moved: false,
        openedFromToggle: true,
      };
      dispatchButtonClick(button);
      setHoveredKey(null);
      // O menu é renderizado depois deste pointerdown. Assim que as opções
      // existem, amplia imediatamente a mais próxima do dedo parado; não é
      // necessário um primeiro movimento para iniciar a seleção deslizante.
      window.requestAnimationFrame(() => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        const nearest = closestDockOption(root, event.clientX, event.clientY);
        setHoveredKey(dockButtonKey(nearest));
      });
      return;
    }
    dragRef.current = {
      root,
      pointerId: event.pointerId,
      pointerType: event.pointerType,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
    };
    setHoveredKey(dockButtonKey(button));
  }, [dispatchButtonClick, onInteraction]);

  const onPointerMove = useCallback((event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    onInteraction?.();
    if (
      Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) >=
      MOVE_THRESHOLD_PX
    ) {
      drag.moved = true;
    }
    const button = dockButtonAt(drag.root, event.clientX, event.clientY);
    setHoveredKey(dockButtonKey(button));
  }, [onInteraction]);

  const onPointerUp = useCallback(
    (event) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      const button = dockButtonAt(drag.root, event.clientX, event.clientY);
      const moved = drag.moved;
      onInteraction?.();
      clearDrag();
      if (drag.openedFromToggle && (!moved || button?.dataset.dockToggle === "true")) {
        suppressClickRef.current = true;
        if (suppressTimerRef.current) window.clearTimeout(suppressTimerRef.current);
        suppressTimerRef.current = window.setTimeout(() => {
          suppressClickRef.current = false;
          suppressTimerRef.current = null;
        }, 400);
        return;
      }
      if (!button) return;

      // Em WKWebView, touch-action:none pode impedir o click sintetizado pelo
      // browser. A ação especial é chamada ainda dentro do gesto físico. Isto
      // é necessário para APIs iOS que exigem user activation (ex.: bússola).
      event.preventDefault();
      if (onTouchOptionSelected?.(button, event) === true) {
        suppressClickRef.current = true;
        if (suppressTimerRef.current) window.clearTimeout(suppressTimerRef.current);
        suppressTimerRef.current = window.setTimeout(() => {
          suppressClickRef.current = false;
          suppressTimerRef.current = null;
        }, 400);
        return;
      }
      dispatchButtonClick(button);
    },
    [clearDrag, dispatchButtonClick, onInteraction, onTouchOptionSelected]
  );

  const onPointerCancel = useCallback(() => clearDrag(), [clearDrag]);

  const onClickCapture = useCallback(
    (event) => {
      // O rato usa exclusivamente o fluxo normal do browser: capture ->
      // onClick do botão -> ação. Não o sujeitar à supressão necessária para
      // o click sintetizado após um gesto tátil.
      if (shouldUseNativeDockClick(lastPointerTypeRef.current)) {
        onInteraction?.();
        return;
      }
      if (suppressClickRef.current && !dispatchingRef.current) {
        suppressClickRef.current = false;
        if (suppressTimerRef.current) {
          window.clearTimeout(suppressTimerRef.current);
          suppressTimerRef.current = null;
        }
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const button = event.target?.closest?.("button");
      // Os painéis do menu são portals React: os eventos continuam a subir
      // pela árvore React, embora os botões já não pertençam ao DOM do Dock.
      // Não os podemos capturar/fechar como se fossem opções da barra.
      const root = event.currentTarget;
      if (
        !button ||
        !root?.contains?.(button) ||
        button.dataset.dockToggle === "true"
      ) {
        return;
      }
      onInteraction?.();
      if (button.dataset.dockKeepOpen === "true") return;
      queueMicrotask(() => onOptionSelected?.(button));
    },
    [onInteraction, onOptionSelected]
  );

  return {
    hoveredKey,
    dockPointerProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      onClickCapture,
    },
  };
}

